import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useLanguage, SUPPORTED_UI_LANGS } from '../../contexts/LanguageContext';
import { Link, useLocation } from 'react-router-dom';

const DashboardHeader = () => {
  const { currentUser, logout } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [kioskActive, setKioskActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const breadcrumbs = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (!parts.length) return ['Dashboard'];
    return ['Home', ...parts.map(formatSegment)];
  }, [location.pathname]);

  const onKioskRoute =
    location.pathname.startsWith('/reception/kiosk') ||
    location.pathname.startsWith('/staff/dashboard');
  const showKioskControls = kioskActive || onKioskRoute;

  useEffect(() => {
    const handler = (event) => setKioskActive(Boolean(event?.detail?.active));
    window.addEventListener('brissbella:kiosk:active', handler);
    return () => window.removeEventListener('brissbella:kiosk:active', handler);
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    onChange();
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen not available:', err);
    }
  };

  const requestKioskLock = () => {
    window.dispatchEvent(new Event('brissbella:kiosk:lock'));
  };

  return (
    <header className="z-20 border-b border-stone-200 bg-white/95 backdrop-blur-sm">
      <div className="px-3 sm:px-6 lg:px-8">
        <div className="flex min-h-[64px] flex-col justify-center gap-3 py-3 lg:min-h-[72px] lg:flex-row lg:items-center lg:justify-between lg:py-0">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className="rounded-2xl p-2 text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
                onClick={toggleSidebar}
              >
                <span className="sr-only">{t('header.openSidebar')}</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>

              <div className="min-w-0">
                <p className="hidden truncate text-sm font-medium text-stone-500 sm:block">
                  {breadcrumbs.join(' > ')}
                </p>
                <p className="truncate text-lg font-semibold text-stone-950 sm:mt-0.5 sm:text-base">
                  {breadcrumbs[breadcrumbs.length - 1]}
                </p>
              </div>
            </div>

            <div className="relative md:hidden">
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-white transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <img
                  src={
                    currentUser?.photoURL ||
                    `https://ui-avatars.com/api/?name=${currentUser?.firstName}+${currentUser?.lastName}&background=DB2777&color=fff`
                  }
                  alt=""
                  className="h-9 w-9 rounded-full"
                />
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 z-50 mt-2 w-[min(100vw-1.5rem,13rem)] rounded-2xl border border-stone-200 bg-white py-1 shadow-[0_18px_40px_-30px_rgba(28,25,23,0.3)] ring-1 ring-black/5">
                  <div className="border-b border-stone-100 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Language
                    </p>
                    <div className="mt-2 flex items-center gap-1 rounded-full bg-stone-50 p-1">
                      {SUPPORTED_UI_LANGS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setLanguage(opt.id)}
                          className={`flex-1 rounded-full px-2.5 py-1.5 text-[11px] font-semibold transition ${
                            language === opt.id
                              ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-200'
                              : 'text-stone-500 hover:text-stone-800'
                          }`}
                        >
                          {opt.short}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    className="block min-h-[40px] px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    {t('nav.profile')}
                  </Link>
                  <Link
                    to="/settings"
                    className="block min-h-[40px] px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    {t('nav.settings')}
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="block min-h-[40px] w-full px-4 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
                  >
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 sm:justify-end sm:gap-3">
            <div className="hidden xl:flex xl:w-[18rem]">
              <div className="flex min-h-[46px] w-full items-center rounded-full border border-stone-200 bg-stone-50 px-4">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-4 w-4 text-stone-400"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                  />
                </svg>
                <span className="ml-3 text-sm text-stone-400">Search workspace...</span>
              </div>
            </div>

            {showKioskControls ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {isFullscreen ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 3v4.5A1.5 1.5 0 0 1 7.5 9H3m18 0h-4.5A1.5 1.5 0 0 1 15 7.5V3M3 15h4.5A1.5 1.5 0 0 1 9 16.5V21m12-6h-4.5A1.5 1.5 0 0 0 15 16.5V21"
                      />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 8V5a1 1 0 0 1 1-1h3m8 0h3a1 1 0 0 1 1 1v3m0 8v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3"
                      />
                    </svg>
                  )}
                </button>
                {kioskActive ? (
                  <button
                    type="button"
                    onClick={requestKioskLock}
                    aria-label="Lock kiosk"
                    title="Lock kiosk"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 10.5V7.5a4.5 4.5 0 0 0-9 0v3M6 10.5h12a1.5 1.5 0 0 1 1.5 1.5v7.5A1.5 1.5 0 0 1 18 21H6a1.5 1.5 0 0 1-1.5-1.5V12A1.5 1.5 0 0 1 6 10.5Z"
                      />
                    </svg>
                  </button>
                ) : null}
              </div>
            ) : null}

            <div
              className="hidden items-center gap-0.5 rounded-full border border-stone-200 bg-stone-50 p-0.5 md:flex"
              role="group"
              aria-label={t('lang.label')}
            >
              {SUPPORTED_UI_LANGS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setLanguage(opt.id)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                    language === opt.id
                      ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-200'
                      : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  {opt.short}
                </button>
              ))}
            </div>

            <div className="relative hidden md:block">
              <button
                type="button"
                className="flex items-center gap-3 rounded-full border border-stone-200 bg-white px-2.5 py-1.5 transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <img
                  src={
                    currentUser?.photoURL ||
                    `https://ui-avatars.com/api/?name=${currentUser?.firstName}+${currentUser?.lastName}&background=DB2777&color=fff`
                  }
                  alt=""
                  className="h-9 w-9 rounded-full"
                />
                <div className="hidden min-w-0 md:flex md:flex-col md:items-start">
                  <span className="max-w-[10rem] truncate text-sm font-semibold text-stone-900">
                    {currentUser?.firstName} {currentUser?.lastName}
                  </span>
                  <span className="text-xs text-stone-500 capitalize">
                    {currentUser?.role}
                  </span>
                </div>
                <svg
                  className="hidden h-5 w-5 text-stone-400 md:block"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 z-50 mt-2 w-[min(100vw-1.5rem,13rem)] rounded-2xl border border-stone-200 bg-white py-1 shadow-[0_18px_40px_-30px_rgba(28,25,23,0.3)] ring-1 ring-black/5 sm:w-52">
                  <Link
                    to="/profile"
                    className="block min-h-[40px] px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    {t('nav.profile')}
                  </Link>
                  <Link
                    to="/settings"
                    className="block min-h-[40px] px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    {t('nav.settings')}
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="block min-h-[40px] w-full px-4 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
                  >
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

const formatSegment = (segment) =>
  (segment === 'instructors' ? 'staff' : segment)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default DashboardHeader;
